import { useCallback, useEffect, useState } from 'react';
import axios from '../config/api';
import type { StaffOrderIssue } from '../components/OrderIssuesPanel';
import { sortNewestFirst } from '../utils/newestFirst';
import {
  getIssueTypeLabel,
  getResolutionLabel,
  getRefundOutcomeLabel,
  ISSUE_STATUS_LABELS,
} from '../content/orderIssueTypes';

function statusLabel(id: string): string {
  return ISSUE_STATUS_LABELS[id] || id.replace(/_/g, ' ');
}

function formatAffectedItems(items: unknown[]): string {
  if (!Array.isArray(items) || items.length === 0) return '';
  return items
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      const row = item as { quantity?: number; name?: string; menu_item_id?: string };
      const qty = row.quantity ?? 1;
      const name = row.name || row.menu_item_id || 'item';
      return `${qty}× ${name}`;
    })
    .filter(Boolean)
    .join('; ');
}

export function issueToExcelRow(issue: StaffOrderIssue): Record<string, string | number> {
  return {
    reported_at: issue.created_at,
    updated_at: issue.updated_at,
    order_number: issue.order_number || issue.order_id.slice(0, 8),
    order_status: issue.order_status || '',
    order_total_jod: Number(issue.total_amount ?? 0).toFixed(2),
    cafe: issue.cafe_name,
    customer_name: issue.user_name,
    customer_email: issue.user_email,
    issue_type: getIssueTypeLabel(issue.issue_type),
    status: statusLabel(issue.status),
    refund_outcome: getRefundOutcomeLabel(issue.refund_outcome || 'pending'),
    resolution: getResolutionLabel(issue.resolution_preference),
    estimated_refund_jod: Number(issue.estimated_refund_amount ?? 0).toFixed(2),
    affected_items: formatAffectedItems(issue.affected_items),
    description: issue.description || '',
    photo_count: Array.isArray(issue.photo_urls) ? issue.photo_urls.length : 0,
  };
}

export function useIssueReports(
  selectedCafe: string,
  start: Date,
  end: Date,
  enabled: boolean
) {
  const [issues, setIssues] = useState<StaffOrderIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setIssues([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        created_from: start.toISOString(),
        created_to: end.toISOString(),
      };
      if (selectedCafe) params.cafe_id = selectedCafe;
      const { data } = await axios.get<{ issues: StaffOrderIssue[] }>(
        '/api/barista/order-issues',
        { params }
      );
      setIssues(sortNewestFirst(data.issues || [], (issue) => issue.created_at));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load issue reports';
      setError(msg);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCafe, start, end, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    issues,
    loading,
    error,
    reload: load,
    count: issues.length,
    subtitle: !enabled
      ? 'Support administrators only'
      : loading
        ? 'Loading…'
        : error
          ? error
          : 'Customer issue reports in the selected period.',
    downloadDisabled: !enabled || loading || !!error,
  };
}
